import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect, useRef } from 'react';
import { SearchParameters, Location, ServiceDefinition, SavedSearch, SavedSearchParameters } from '../models';
import { localStorageManager } from '../services/LocalStorageManager';
import { apiClient } from '../services/ApiClient';
import { savedSearchMigrator } from '../services/SavedSearchMigrator';
import { v4 as uuidv4 } from 'uuid';

interface SearchContextType {
  // Search parameter state
  searchParams: SearchParameters | null;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParameters | null>>;
  updateLocation: (location: Location | null) => void;
  updateDate: (date: Date) => void;
  updateServices: (services: ServiceDefinition[]) => void;
  clearSearchParams: () => void;
  clearSearchResults?: () => void; // Optional because it will be set after the Provider is created
  
  // Saved searches state
  savedSearches: SavedSearch[];
  currentSavedSearch: SavedSearch | null;
  isLoading: boolean;
  error: string | null;
  searchLocationChanged: boolean;
  
  // Saved searches functions
  saveCurrentSearch: (name: string, autoSave?: boolean) => Promise<boolean>;
  loadSearch: (searchId: string) => Promise<boolean>;
  updateLoadedSearch: () => Promise<boolean>;
  deleteSearch: (searchId: string) => boolean;
  toggleAutoSave: (searchId: string) => boolean;
  clearLoadedSearch: () => void;
  checkAndAutoUpdateSearch: () => Promise<boolean>;
}

const SearchContext = createContext<SearchContextType | null>(null);

export const SearchProvider: React.FC<{
  children: ReactNode,
  clearSearchResults?: () => void // Accept the clearSearchResults function as a prop
}> = ({ children, clearSearchResults }) => {
  // Central state for search parameters
  const [searchParams, setSearchParams] = useState<SearchParameters | null>(null);
  
  // State for saved searches (from useSearchManagement)
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [currentSavedSearch, setCurrentSavedSearch] = useState<SavedSearch | null>(null);
  const [searchLocationChanged, setSearchLocationChanged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Ref to prevent running repair multiple times
  const repairRan = useRef(false);
  
  // Effect for Initial Load and Migration/Validation of saved searches
  useEffect(() => {
    const loadAndMigrate = async () => {
      if (repairRan.current) return; // Prevent running multiple times
      repairRan.current = true;

      setIsLoading(true);
      setError(null);
      let finalSearches: Record<string, SavedSearch> = {};

      try {
        // 1. Get raw data
        const rawData = localStorageManager.getSavedSearches();

        // 2. Migrate and validate using the dedicated service
        const result = await savedSearchMigrator.migrateAndValidateAll(rawData);
        finalSearches = result.migratedSearches;
        const migrationStats = result.stats;

        // 3. Update state with the validated/migrated searches
        setSavedSearches(Object.values(finalSearches));

        // 4. Save back to localStorage ONLY if changes were made during migration/repair
        const changesMade = migrationStats.repaired > 0 || migrationStats.removed > 0;
        if (changesMade) {
          console.log("Saving migrated/repaired searches back to storage.");
          localStorageManager.saveRawSearches(finalSearches);
        }

        // 5. Report stats (optional)
        if (migrationStats.examined > 0) {
           if (changesMade) {
             console.warn(`Saved search migration/validation complete: ${migrationStats.repaired} repaired/migrated, ${migrationStats.removed} removed. Details:`, migrationStats.details);
           } else {
             console.info(`Saved search validation complete: ${migrationStats.examined} searches checked, no issues found.`);
           }
        }

      } catch (err) {
        console.error("Error during initial load/migration:", err);
        setError("Failed to load or migrate saved searches.");
        setSavedSearches([]); // Set to empty on critical error
      } finally {
        setIsLoading(false);
      }
    };

    loadAndMigrate();
  }, []); // Empty dependency array ensures it runs only once on mount
  
  // Check if location changed when searchParams or currentSavedSearch changes
  useEffect(() => {
    const currentContextLocationId = searchParams?.location?.id ?? null;
    if (currentSavedSearch && currentContextLocationId !== currentSavedSearch.parameters.locationId) {
      setSearchLocationChanged(true);
    } else if (currentSavedSearch && currentContextLocationId === currentSavedSearch.parameters.locationId) {
      setSearchLocationChanged(false);
    }
  }, [searchParams, currentSavedSearch]);
  
  // Convenience method to update just the location
  const updateLocation = useCallback((location: Location | null) => {
    if (!location) {
      setSearchParams(null);
      return;
    }
    
    setSearchParams(prev => ({
      ...(prev ?? { dateOfInterest: new Date(), services: [] }),
      location
    }));
  }, []);
  
  // Convenience method to update just the date
  const updateDate = useCallback((date: Date) => {
    setSearchParams(prev => {
      if (!prev || !prev.location) {
        return prev;
      }
      return {
        ...prev,
        dateOfInterest: date
      };
    });
  }, []);
  
  // Convenience method to update just the services
  const updateServices = useCallback((services: ServiceDefinition[]) => {
    setSearchParams(prev => {
      if (!prev || !prev.location) {
        return prev;
      }
      return {
        ...prev,
        services
      };
    });
  }, []);
  
  // Method to clear search parameters
  const clearSearchParams = useCallback(() => {
    setSearchParams(null);
  }, []);
  
  // Save the current search parameters as a new saved search
  const saveCurrentSearch = useCallback(async (name: string, autoSave: boolean = true): Promise<boolean> => {
    // Read current parameters from context
    if (!searchParams || !searchParams.location || !searchParams.services || searchParams.services.length === 0) {
      setError('Cannot save: Please select a location, date, and at least one service.');
      return false;
    }

    // Basic validation
    if (!searchParams.location.id) {
      setError('Cannot save: Location ID is missing.');
      return false;
    }
    if (searchParams.location.coordinates &&
        searchParams.location.coordinates.latitude === 0 &&
        searchParams.location.coordinates.longitude === 0) {
      setError('Cannot save: Invalid location coordinates (0,0).');
      return false;
    }

    // Uniqueness Check
    const trimmedName = name.trim();
    const isDuplicate = savedSearches.some(
      s => s.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setError(`A search named "${trimmedName}" already exists. Please choose a unique name.`);
      return false;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Construct the parameters object for saving
      const parametersToSave: SavedSearchParameters = {
        locationId: searchParams.location.id,
        dateOfInterest: searchParams.dateOfInterest.toISOString().split('T')[0], // Format date
        services: searchParams.services,
      };

      // Use the basic save method from localStorageManager
      const newSearch = localStorageManager.saveSearch({
        name: name,
        parameters: parametersToSave,
        autoSave: autoSave
      });

      // Update state
      setSavedSearches(prev => [...prev, newSearch]);
      setCurrentSavedSearch(newSearch); // Automatically load the newly saved search
      setSearchLocationChanged(false);

      return true;
    } catch (err) {
      console.error('Error saving search:', err);
      setError('Failed to save search. Please try again later.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, savedSearches]);

  // Load a saved search
  const loadSearch = useCallback(async (searchId: string): Promise<boolean> => {
    const searchToLoad = savedSearches.find(s => s.id === searchId);
    if (!searchToLoad) {
      setError(`Search with ID ${searchId} not found.`);
      return false;
    }

    // Validate required fields before proceeding
    if (!searchToLoad.parameters || !searchToLoad.parameters.locationId || !searchToLoad.parameters.dateOfInterest || !searchToLoad.parameters.services) {
        setError(`Cannot load search "${searchToLoad.name}": Invalid or incomplete data.`);
        return false;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Fetch full location details from API using the stored ID
      console.log('Loading location data...');
      const fetchedLocation = await apiClient.getLocationById(searchToLoad.parameters.locationId);
      if (!fetchedLocation || !fetchedLocation.id || !fetchedLocation.name || !fetchedLocation.coordinates) {
        throw new Error('Incomplete location data received from API');
      }
      console.log(`Location "${fetchedLocation.name}" loaded`);

      // Prepare the SearchParameters object for the context
      const loadedParams: SearchParameters = {
        location: fetchedLocation, // Use the full fetched location object
        dateOfInterest: new Date(searchToLoad.parameters.dateOfInterest + 'T00:00:00Z'), // Parse date string correctly
        services: searchToLoad.parameters.services
      };

      // Update the SearchContext
      setSearchParams(loadedParams);

      // Update internal state
      setCurrentSavedSearch(searchToLoad);
      setSearchLocationChanged(false);

      return true;
    } catch (error) {
      console.error('Failed to load search:', error);
      setError(`Could not load search "${searchToLoad.name}". Failed to fetch location data.`);
      setCurrentSavedSearch(null); // Clear loaded search ref on failure
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [savedSearches]);

  // Update the currently loaded search with the current parameters
  const updateLoadedSearch = useCallback(async (): Promise<boolean> => {
    if (!currentSavedSearch) {
      setError('No search is currently loaded to update.');
      return false;
    }
    if (!searchParams || !searchParams.location || !searchParams.services || searchParams.services.length === 0) {
      setError('Cannot update: Current parameters are incomplete.');
      return false;
    }
    if (searchParams.location.coordinates &&
        searchParams.location.coordinates.latitude === 0 &&
        searchParams.location.coordinates.longitude === 0) {
      setError('Cannot update: Invalid location coordinates (0,0).');
      return false;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Construct the parameters object for updating
      const parametersToUpdate: SavedSearchParameters = {
        locationId: searchParams.location.id,
        dateOfInterest: searchParams.dateOfInterest.toISOString().split('T')[0],
        services: searchParams.services,
      };

      // Use the basic update method from localStorageManager
      const updatedSearch = localStorageManager.updateSearch(
        currentSavedSearch.id,
        {
          name: currentSavedSearch.name,
          parameters: parametersToUpdate,
          autoSave: currentSavedSearch.autoSave
        }
      );

      if (updatedSearch) {
        // Update state
        setSavedSearches(prev =>
          prev.map(s => s.id === currentSavedSearch.id ? updatedSearch : s)
        );
        setCurrentSavedSearch(updatedSearch);
        setSearchLocationChanged(false);

        return true;
      } else {
        setError('Failed to update search: Search not found in storage.');
        setCurrentSavedSearch(null);
        return false;
      }
    } catch (err) {
      console.error('Error updating search:', err);
      setError('Failed to update search. Please try again later.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentSavedSearch, searchParams]);

  // Delete a saved search
  const deleteSearch = useCallback((searchId: string): boolean => {
    setIsLoading(true);
    setError(null);
    try {
      const searchToDelete = savedSearches.find(s => s.id === searchId);
      localStorageManager.deleteSavedSearch(searchId);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId));

      // If the deleted search was the currently loaded one, clear it
      if (currentSavedSearch?.id === searchId) {
        setCurrentSavedSearch(null);
      }

      return true;
    } catch (err) {
      console.error('Error deleting search:', err);
      setError('Failed to delete search. Please try again later.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [savedSearches, currentSavedSearch?.id]);

  // Toggle auto-save for a specific search ID
  const toggleAutoSave = useCallback((searchId: string): boolean => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedSearch = localStorageManager.toggleAutoSave(searchId);
      if (updatedSearch) {
        setSavedSearches(prev =>
          prev.map(s => s.id === searchId ? updatedSearch : s)
        );
        // If the toggled search is the currently loaded one, update its state too
        if (currentSavedSearch?.id === searchId) {
          setCurrentSavedSearch(updatedSearch);
        }
        return true;
      } else {
         setError(`Failed to toggle auto-save: Search ID ${searchId} not found.`);
         return false;
      }
    } catch (err) {
      console.error('Error toggling auto-save:', err);
      setError('Failed to update auto-save setting. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentSavedSearch?.id]);

  // Clear the currently loaded search reference
  const clearLoadedSearch = useCallback(() => {
    setCurrentSavedSearch(null);
    setSearchLocationChanged(false);
    clearSearchParams();
  }, [clearSearchParams]);

  // Check if the currently loaded search should be auto-updated
  const checkAndAutoUpdateSearch = useCallback(async () => {
    // Use current context parameters
    const currentContextParams = searchParams;
    const currentContextLocationId = currentContextParams?.location?.id ?? null;

    // Conditions for auto-update
    if (
        currentSavedSearch &&
        currentSavedSearch.autoSave &&
        currentContextParams &&
        currentContextLocationId &&
        currentContextParams.services &&
        currentContextParams.services.length > 0
       )
    {
        // Check if parameters have actually changed compared to the loaded search
        const paramsChanged =
            currentContextLocationId !== currentSavedSearch.parameters.locationId ||
            currentContextParams.dateOfInterest.toISOString().split('T')[0] !== currentSavedSearch.parameters.dateOfInterest ||
            JSON.stringify(currentContextParams.services) !== JSON.stringify(currentSavedSearch.parameters.services);

        if (!paramsChanged) {
            return false; // No need to update if nothing changed
        }

        // Proceed with update if changes detected
        setIsLoading(true);
        setError(null);
        try {
            // Validate coordinates before saving
            if (currentContextParams.location?.coordinates &&
                currentContextParams.location.coordinates.latitude === 0 &&
                currentContextParams.location.coordinates.longitude === 0) {
              console.warn('Auto-update skipped: Invalid location coordinates (0,0).');
              return false;
            }

            // Construct the parameters object for updating
            const parametersToUpdate: SavedSearchParameters = {
              locationId: currentContextLocationId,
              dateOfInterest: currentContextParams.dateOfInterest.toISOString().split('T')[0],
              services: currentContextParams.services,
            };

            // Use the basic update method
            const updatedSearch = localStorageManager.updateSearch(
              currentSavedSearch.id,
              {
                name: currentSavedSearch.name,
                parameters: parametersToUpdate,
                autoSave: currentSavedSearch.autoSave
              }
            );

            if (updatedSearch) {
              // Update state
              setSavedSearches(prev =>
                prev.map(s => s.id === currentSavedSearch.id ? updatedSearch : s)
              );
              setCurrentSavedSearch(updatedSearch);
              setSearchLocationChanged(false);

              return true;
            } else {
               console.warn(`Auto-update failed: Search ID ${currentSavedSearch.id} not found in storage.`);
               return false;
            }
        } catch (err) {
            console.error('Error auto-updating search:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }
    return false; // Conditions for auto-update not met
  }, [currentSavedSearch, searchParams]);
  
  return (
    <SearchContext.Provider value={{ 
      // Search parameter state and functions
      searchParams, 
      setSearchParams,
      updateLocation,
      updateDate,
      updateServices,
      clearSearchParams,
      clearSearchResults,
      
      // Saved searches state
      savedSearches,
      currentSavedSearch,
      isLoading,
      error,
      searchLocationChanged,
      
      // Saved searches functions
      saveCurrentSearch,
      loadSearch,
      updateLoadedSearch,
      deleteSearch,
      toggleAutoSave,
      clearLoadedSearch,
      checkAndAutoUpdateSearch
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};
